import {Addressable} from './api-common.ts';
import {BASE_API_URL} from '../envs.ts';
import type {FeatureLike} from 'ol/Feature';
import {replaceAll} from '../helpers/string.ts';
import type {LanguageCode} from '../i18n.ts';
import {getLanguageCode} from '../i18n.ts';
import type {Detour} from './api-goodtoknow.ts';
import type {AdditionalLayerType, StationCode} from '../layers/layerTypes.ts';

// describes bicycle service and rental
export class Bicycle extends Addressable {
  id: number;
  refid: number;
  title: string;
  abstract?: string;
  abstractReturn?: string;
  organisation: string;
  type: 'cycleservice' | 'velorental';

  constructor(data: FeatureLike | Bicycle) {
    super(data);
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      this.id = Number(data.getId());
      this.refid = Number(data.get('id'));
      this.title = data.get('title');
      this.abstract = data.get('abstract');
      this.abstractReturn = data.get('abstract_return');
      this.organisation = data.get('organisation');
      this.type =
        data.get('category') === 'rental' ? 'velorental' : 'cycleservice';
    } else {
      Object.assign(this, <Bicycle>data);
    }
  }
}

export class Accommodation extends Addressable {
  id: number;
  refid: number;
  title: string;
  types: string[];
  photo: string;
  photoUrls: Record<string, string>;
  photoBig: string;
  photoSmall: string;
  photoAbstract: string;
  starsPara?: number;
  starsHotel?: number;
  beds?: number;
  campingPlaces?: number;
  priceFrom?: number;
  priceTo?: number;
  hotelGroup?: string;

  constructor(data: FeatureLike | Accommodation) {
    super(data);
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      this.id = Number(data.getId());
      this.title = data.get('name');
      this.types = data.get('types');
      this.photo = data.get('foto');
      this.starsPara = Number(data.get('stars_parahotellerie'));
      this.starsHotel = Number(data.get('stars_hotellerie'));
      this.beds = data.get('beds');
      this.campingPlaces = data.get('campingplaces');
      this.priceFrom = data.get('pricefrom');
      this.priceTo = data.get('priceto');
      this.hotelGroup = data.get('hotelgroup');
    } else {
      Object.assign(this, <Accommodation>data);
    }
  }
}

export class Bookable extends Addressable {
  id: number;
  title: string;
  photo: string;
  photoUrls: Record<string, string>;
  photoBig: string;
  photoSmall: string;
  photoAbstract: string;
  text: string;
  urlBooking?: string;

  constructor(data: Bookable) {
    super(data);
    Object.assign(this, <Bookable>data);
    if (this.urlBooking?.length) this.urlBookingLink = this.urlBooking;
  }
}

export class Swisspark {
  id: number;
  refid: number;
  title: string;
  abstract: string;
  photo: string;
  photoUrls: Record<string, string>;
  photoBig: string;
  photoSmall: string;
  photoAbstract: string;
  url: string;

  constructor(data: FeatureLike | Swisspark) {
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      this.id = Number(data.getId());
      this.refid = Number(data.get('id'));
      this.title = data.get('title');
      this.abstract = data.get('abstract');
      this.photo = data.get('foto');
      this.url = data.get('url');
    } else {
      Object.assign(this, <Swisspark>data);
    }
  }
}

export class WildlifeArea {
  id: number;
  title: string;
  subtitle: string;
  dispositions: string;
  protectionState: string;
  protectionPeriod: string;
  moreInfo?: string;

  constructor(data: FeatureLike) {
    const languageCode = getLanguageCode();
    // we don't have english translations from swisstopo, switch to german
    const languageKey = languageCode === 'en' ? 'de' : languageCode;
    this.id = data.get('id');
    this.title = data.get('label');
    this.dispositions = data.get(`best_${languageKey}`);
    this.protectionState = data.get(`schutzs_${languageKey}`);
    this.protectionPeriod = data.get('schutzzeit');
    this.moreInfo = data.get('zusatzinformation');
    this.subtitle = data.get('subtitle');
  }
}

export class WildlifeRoute {
  id: number;
  type: string;
  title: string;
  limitations: string;
  subtitle: string;

  constructor(data: FeatureLike) {
    const languageCode = getLanguageCode();
    // we don't have english translations from swisstopo, switch to german
    const languageKey = languageCode === 'en' ? 'de' : languageCode;
    this.type = data.get(`wegtyp_${languageKey}`);
    this.title = data.get('label');
    this.limitations = data.get('einschraenkungen');
    this.subtitle = data.get('subtitle');
    this.id = data.get('id');
  }
}

export class AlpguardDog {
  id: number;
  title: string;
  htmlPopup: string;

  constructor(data: FeatureLike) {
    this.title = data.get('label');
    this.htmlPopup = data.get('htmlPopup');
    this.id = data.get('id');
  }
}

export class Station {
  id: number;
  refid: number;
  title: string;
  subtitle: StationCode;

  constructor(data: FeatureLike | Station) {
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      this.id = Number(data.getId());
      this.refid = Number(data.get('id'));
      this.title = data.get('title');
      this.subtitle = data.get('sc_transport');
    } else {
      Object.assign(this, data);
    }
  }
}

export class Slowup {
  id: number;
  refid: number;
  title: string;
  date: string;
  photo: string;
  photoUrls: Record<string, string>;
  photoBig: string;
  photoSmall: string;
  photoAbstract: string;
  urlLink: string;
  urlText: string;

  constructor(data: FeatureLike | Slowup) {
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      this.id = Number(data.getId());
      this.refid = Number(data.get('id'));
      this.title = data.get('title');
      this.date = data.get('date');
      this.photo = data.get('foto');
      this.photoAbstract = data.get('abstract');
      this.urlLink = data.get('url1_link');
      this.urlText = data.get('url1_text');
    } else {
      Object.assign(this, <Slowup>data);
    }
    if (this.date)
      this.date = replaceAll(
        new Date(this.date).toLocaleDateString('en-GB'),
        '/',
        '.',
      );
  }
}

export class Shooting {
  id: number;
  title: string;
  htmlPopup: string;

  constructor(data: FeatureLike) {
    this.title = data.get('label');
    this.htmlPopup = data.get('htmlPopup');
    this.id = data.get('id');
  }
}

export class Municipality {
  id: number;
  title: string;
  htmlPopup: string;

  constructor(data: FeatureLike) {
    this.title = data.get('label');
    this.htmlPopup = data.get('htmlPopup');
    this.id = data.get('id');
  }
}

export class Canton {
  id: number;
  title: string;
  htmlPopup: string;

  constructor(data: FeatureLike) {
    this.title = data.get('label');
    this.htmlPopup = data.get('htmlPopup');
    this.id = data.get('id');
  }
}

export type AdditionalLayerData =
  | Bicycle
  | Accommodation
  | Detour
  | Swisspark
  | WildlifeArea
  | WildlifeRoute
  | AlpguardDog
  | Station
  | Slowup
  | Bookable
  | Municipality
  | Canton
  | Shooting;

export async function fetchAdditionalLayer<T>(
  ids: number[],
  type: AdditionalLayerType,
  lang: LanguageCode,
): Promise<T[]> {
  if (type === 'station') {
    return fetchAdditionalLayerByRefid<T>(ids, type, lang);
  }
  const response = await fetch(
    `${BASE_API_URL}/api/4/feature/${type}/${ids.join(',')}?lang=${lang}`,
  );
  return (await response.json()).map((poi: AdditionalLayerData | Bookable) => {
    if (type === 'accommodation') return new Accommodation(<Accommodation>poi);
    if (type === 'bookable') return new Bookable(<Bookable>poi);
    if (type === 'swisspark') return new Swisspark(<Swisspark>poi);
    if (type === 'slowup') return new Slowup(<Slowup>poi);
    else return new Bicycle({...(<Bicycle>poi), type});
  });
}

export async function fetchAdditionalLayerByRefid<T>(
  refids: number[],
  type: AdditionalLayerType,
  lang: LanguageCode,
): Promise<T[]> {
  const response = await fetch(
    `${BASE_API_URL}/api/4/feature/${type}/refid/${refids.join(',')}?lang=${lang}`,
  );
  return (await response.json()).map((poi: AdditionalLayerData) => {
    if (type === 'station') return new Station(<Station>poi);
    if (type === 'swisspark') return new Swisspark(<Swisspark>poi);
    if (type === 'cycleservice' || type === 'velorental')
      return new Bicycle({...(<Bicycle>poi), type});
    if (type === 'accommodation') return new Accommodation(<Accommodation>poi);
    return undefined;
  });
}
